import { Checkbox } from 'framework7-react';
import { ComponentType, FC, ReactNode, useCallback, useMemo, useState } from 'react';

import { CurrentCity } from '@hooks/useCurrentUserLocation';

import Button from '@components/buttons/Button';
import { FormLabel } from '@components/form/FormLabel';
import { Input } from '@components/form/Input';
import { List } from '@components/layout/List/List';
import { PopoverWrapper } from '@components/layout/Popover/PopoverWrapper';

export const MultiSelectField: FC<{
  name?: string;
  isSingleSelect?: boolean;
  placeholder?: string;
  label?: string;
  defaultValue: string[];
  options: { value: string; label: string }[];
  onChange: (updatedValues: string[]) => void;
  EmptyStateComponent?: ComponentType<{ searchValue?: string; onClose: () => void }>;
  allowSearch?: boolean;
  triggerNode?: ReactNode;
  defaultOpen?: boolean;
}> = ({
  isSingleSelect,
  name,
  placeholder,
  label,
  defaultValue,
  options,
  onChange,
  EmptyStateComponent,
  allowSearch,
  triggerNode,
  defaultOpen,
}) => {
  const [selectedRecord, setSelectedRecord] = useState<Record<string, boolean>>(
    Object.fromEntries(defaultValue.map((value) => [value, true])),
  );

  const handleCheckboxChange = useCallback(
    (value: string, onClose: () => void) => (updatedCheckedValue: boolean) => {
      setSelectedRecord((prev) => {
        const updatedRecord = isSingleSelect
          ? { [value]: true }
          : { ...prev, [value]: updatedCheckedValue };

        onChange(
          Object.entries(updatedRecord)
            .filter(([, checked]) => checked)
            .map(([value]) => value),
        );

        return updatedRecord;
      });

      if (isSingleSelect) {
        onClose();
      }
    },
    [],
  );

  const selectedOptions = useMemo(() => {
    return options.filter(({ value }) => selectedRecord[value]);
  }, [options, selectedRecord]);

  const inputFieldValue = useMemo(() => {
    return selectedOptions
      .map(({ label }) => label)
      .sort()
      .join(', ');
  }, [selectedOptions]);

  const PopoverContent = ({ onClose }: { onClose: () => void }) => {
    const [searchValue, setSearchValue] = useState('');

    const filteredOptions = useMemo(() => {
      const searchValueLowercased = searchValue.trim().toLowerCase();
      return options.filter((option) => option.label.toLowerCase().includes(searchValueLowercased));
    }, [options, searchValue]);

    return (
      <div data-testid={`${name}-popoverContent`}>
        {allowSearch && (
          <div className="" style={{ borderBottom: '1px solid rgba(255,255,255,.2)' }}>
            <Input
              placeholder="Search"
              style={{ background: 'transparent' }}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
        )}
        <div style={{ height: 200, overflow: 'auto' }}>
          {!filteredOptions.length && !selectedOptions.length && EmptyStateComponent ? (
            <EmptyStateComponent searchValue={searchValue} onClose={onClose} />
          ) : (
            <List
              listItems={
                filteredOptions.length
                  ? filteredOptions.map((options) => ({
                      defaultChecked: selectedRecord[options.value],
                      value: options.value,
                      title: options.label,
                      ...(isSingleSelect
                        ? {
                            onClick: () => handleCheckboxChange(options.value, onClose)(true),
                            noChevron: true,
                            endIcon: selectedRecord[options.value] ? (
                              <Checkbox defaultChecked />
                            ) : null, // This doesn't actually work because of memoization
                          }
                        : {
                            hasCheckbox: true,
                            onCheckboxChange: handleCheckboxChange(options.value, onClose),
                          }),
                    }))
                  : [{ title: 'No matches' }]
              }
            />
          )}
        </div>
        {!isSingleSelect && (
          <div className="p-2">
            <Button roundness="rounded" onClick={onClose}>
              Close
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {label && <FormLabel text={label} />}
      <PopoverWrapper isVisible={defaultOpen} PopoverContent={PopoverContent}>
        {triggerNode ?? (
          <Input
            name={name}
            placeholder={placeholder}
            value={inputFieldValue}
            onChange={() => {}}
          />
        )}
      </PopoverWrapper>
    </div>
  );
};

export const CitiesField: FC<{
  name?: string;
  cities: CurrentCity[];
  cityIds?: string[];
  onChange: (updatedCityIds: string[]) => void;
}> = ({ name, cities, cityIds: initialCityIds = [], onChange }) => {
  return (
    <MultiSelectField
      name={name}
      label="City *"
      defaultValue={initialCityIds}
      options={cities.map((city) => ({
        value: city._id,
        label: city.name,
      }))}
      onChange={onChange}
    />
  );
};
